import React, { useEffect, forwardRef, useImperativeHandle, RefAttributes } from "react";
import { MultiSelect, MultiSelectChangeEvent, MultiSelectFilterChangeEvent, MultiSelectPageChangeEvent } from "@progress/kendo-react-dropdowns";
import InputGroup from 'react-bootstrap/InputGroup';
import Dropdown from 'react-bootstrap/Dropdown';
import { SvgIcon } from "@progress/kendo-react-common";
import * as svgIcons from "@progress/kendo-svg-icons";
import { Trans } from 'react-i18next';
import { useODataContext } from "../contexts/ODataContext";
import { State } from '@progress/kendo-data-query';


interface SearchTagsProps extends RefAttributes<SearchTagHandle> {
  Label: string;
  PlaceHolder: string;
  TextField: string;
  KeyField: string;
  PageSize: number;
  onFilterTypeChange: (filter: number) => void;
  onSetValue: (event: MultiSelectChangeEvent) => void;
  width?: string;
  enableFilter?: boolean;
  customedTags?: boolean;
  isDisabled?: boolean;
}

export type SearchTagHandle = {
  setValueFilter: (valueFilter: string[], filterType: number) => void;
  setSelectedValue: (value: any[]) => void;
};

const SearchTags: React.FC<SearchTagsProps> = forwardRef<SearchTagHandle, SearchTagsProps>((props, ref) => {

  const delay = 500;
  const { data, total, loadData } = useODataContext();
  const [value, setValue] = React.useState<any[]>([]);
  const [dataState, setDataState] = React.useState<State>({ take: props.PageSize, skip: 0, sort: [{ field: props.TextField, dir: "asc" }] });
  const timeout = React.useRef<any>(false);
  const enableFilter = props.enableFilter !== undefined? props.enableFilter: true;
  
  const onChange = (event: MultiSelectChangeEvent) => {
    props.onSetValue(event);
    setValue(event.target.value);
  }

  const setSelectedValue = (values: any[]) => {
   setValue(values);
  }

  const [filterTypeActive, setFilterTypeActive] = React.useState<string>("and");

  useImperativeHandle(ref, () => ({
    setValueFilter,
    setSelectedValue
  }));

  const setValueFilter = (value: string[], filterType: number) => {
    loadData(dataState).then((res: any[]) => {
      const matchedObjects = res.filter(obj => value.includes(obj.Description));
      setValue(matchedObjects);
      setFilterTypeActive(filterType === 1 ? "and" : "or");
    });
  }

  useEffect(() => {
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      loadData(dataState);
    }, delay);
    return () => clearTimeout(timeout.current);
  }, [dataState]);


  const onFilterTypeChangeActive = (filter: string) => {
    setFilterTypeActive(filter);
  }

  const onFilterChange = (event: MultiSelectFilterChangeEvent) => {
    if (event.filter?.value === "") {
      dataState.filter = undefined;
      setDataState(dataState);
      loadData(dataState);
    } else {
      event.filter.value = event.filter.value.toLowerCase();
      dataState.skip = 0;
    }

    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      dataState.filter = {
        logic: "or",
        filters: [event.filter]
      }
      setDataState(dataState);
      loadData(dataState);
    }, delay);
  }

  const onPageChange = (event: MultiSelectPageChangeEvent) => {
    const { skip, take } = event.page;
  
    if (dataState.skip === skip && dataState.take === take) return;
  
    setDataState(prev => ({
      ...prev,
      skip,
      take
    }));
  };

  const itemWithTooltip = (li : any, itemProps: any) => {
    const itemText = itemProps.dataItem[props.TextField];

    return React.cloneElement(
      li,
      {
        ...li.props,
        title: itemText
      },
      itemText
    );
  };

  const selected = value.length;
  return (
    <div>
      {props.Label?<span className="subtitle">{props.Label}:</span>:null}
      <InputGroup className="mb-6">
        <MultiSelect
          data={data}
          value={value}
          dataItemKey={props.KeyField}
          textField={props.TextField}
          onChange={onChange}
          filterable={true}
          onFilterChange={onFilterChange}
          placeholder={props.PlaceHolder}
          onPageChange={onPageChange}
          disabled={props.isDisabled ?? false}
          virtual={{
            pageSize: props.PageSize,
            skip: dataState.skip ?? 0,
            total: total,
          }}
          tags={ 
            props.customedTags?
            (selected > 0 ? [{ text: `${selected} items`, data: [...value] }] : []

            )
            :(undefined)
          }
          itemRender={itemWithTooltip}
          style={{ width: props.width ?? "80%" }}
        />
        {enableFilter &&(
        <Dropdown className="filter-type-dropdown">
          <Dropdown.Toggle className="k-button k-button-md k-button-solid k-button-solid-secondary k-rounded-md" id="dropdown-basic">
            <SvgIcon icon={svgIcons.filterIcon} size="medium" />
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <Dropdown.Item className={filterTypeActive === "and" ? "active" : ""} eventKey="and" onClick={() => { props.onFilterTypeChange(1); onFilterTypeChangeActive("and") }} ><Trans>filterType.and</Trans></Dropdown.Item>
            <Dropdown.Item className={filterTypeActive === "or" ? "active" : ""} eventKey="or" onClick={() => { props.onFilterTypeChange(2); onFilterTypeChangeActive("or") }}><Trans>filterType.or</Trans></Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
        )}
      </InputGroup>
    </div>
  );
});
export default SearchTags;